import {
    Step,
    StepIndicator,
    Stepper,
    StepSeparator,
    StepStatus,
    UnorderedList,
    useColorMode,
    useSteps,
    VStack
} from '@chakra-ui/react'
import { Body } from '@missionlabs/react'
import { CloseCircleIcon, TickCircleIcon } from '@missionlabs/react/circleloop'
import { ProvisioningFlow, ProvisioningFlowLine } from '@missionlabs/types'
import { FC, Fragment } from 'react'

export interface DeviceProvisioningFlowProps {
    steps: ProvisioningFlow[]
    stepIndex: number
}

export const ProvisioningFlowLines: FC<{ lines?: ProvisioningFlowLine[] }> = ({ lines }) => {
    const { colorMode } = useColorMode()

    if (!lines?.length) return null

    return (
        <>
            {lines.map((line, lineIndex) => (
                <Fragment key={`line-${lineIndex}-${line.text}`}>
                    <Body size="sm">{line.text}</Body>
                    {line.bullets && (
                        <UnorderedList ml="24px">
                            {line.bullets.map((bullet, bulletIndex) => (
                                <Body
                                    key={`bullet-${bulletIndex}-${bullet.text ?? bullet.link}`}
                                    as="li"
                                    size="sm"
                                    sx={{
                                        '&::marker': {
                                            color: `${colorMode}.coolGrey.50`
                                        }
                                    }}
                                    _notLast={{ mb: '4px' }}
                                >
                                    {bullet.text}&nbsp;
                                    {bullet.link && (
                                        <Body
                                            as="a"
                                            // @ts-ignore
                                            href={bullet.link}
                                            target="_blank"
                                            variant="link"
                                            size="sm"
                                        >
                                            {bullet.linkText}
                                        </Body>
                                    )}
                                </Body>
                            ))}
                        </UnorderedList>
                    )}
                </Fragment>
            ))}
        </>
    )
}

export const DeviceProvisioningFlow: FC<DeviceProvisioningFlowProps> = ({ steps, stepIndex }) => {
    const { colorMode } = useColorMode()

    const { activeStep } = useSteps({ index: stepIndex, count: steps.length })

    return (
        <Stepper
            index={activeStep}
            orientation="vertical"
            sx={{ '--stepper-accent-color': 'none', '--stepper-indicator-size': '24px', gap: 0 }}
            data-testid="device-provisioning-flow"
        >
            {steps.map((item, index) => (
                <Step
                    key={`${index}-${item.name}`}
                    // @ts-ignore chakra doesn't realise this is valid
                    _notLast={{ pb: '24px' }}
                >
                    <StepIndicator border="none">
                        <StepStatus
                            active={<CloseCircleIcon boxSize="full" color="#DA0075" />}
                            incomplete={
                                <CloseCircleIcon
                                    boxSize="full"
                                    color={`${colorMode}.coolGrey.60`}
                                />
                            }
                            complete={<TickCircleIcon boxSize="full" color="#3EBC9E" />}
                        />
                    </StepIndicator>

                    <VStack spacing="16px" align="inherit">
                        <Body variant="bold">{item.name}</Body>
                        <ProvisioningFlowLines lines={item.lines} />
                    </VStack>

                    <StepSeparator
                        style={{
                            maxHeight: 'calc(100% - var(--stepper-indicator-size))',
                            top: 'var(--stepper-indicator-size)',
                            background: 'none',
                            border: '1px dashed',
                            borderColor: `var(--chakra-colors-${colorMode}-coolGrey-40)`
                        }}
                    />
                </Step>
            ))}
        </Stepper>
    )
}
